import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, FileText, UserCheck, UserX, Activity, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminMetrics } from '../utils/api';
import AgentLayout from '../components/AgentLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AdminMetrics = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
      navigate('/agent/dashboard'); return;
    }
    loadMetrics(token);
  }, [navigate]);

  const loadMetrics = async (token) => {
    try {
      setIsLoading(true);
      const result = await getAdminMetrics(token);
      if (result.success) setMetrics(result.metrics);
    } catch (e) {
      toast.error('Failed to load metrics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !metrics) {
    return (
      <AgentLayout title="Metrics">
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>
      </AgentLayout>
    );
  }

  const kpis = [
    { label: 'Total Agents', value: metrics.totalAgents, icon: Users, color: 'bg-blue-500' },
    { label: 'Active Agents', value: metrics.activeAgents, icon: UserCheck, color: 'bg-green-500' },
    { label: 'Pending Approval', value: metrics.pendingAgents, icon: UserX, color: 'bg-yellow-500' },
    { label: 'Deactivated', value: metrics.deactivatedAgents, icon: UserX, color: 'bg-red-500' },
    { label: "Today's Logins", value: metrics.todayLogins, icon: Activity, color: 'bg-purple-500' },
    { label: 'Reports (This Month)', value: metrics.reportsThisMonth, icon: FileText, color: 'bg-brand-blue' },
    { label: 'Leads (This Month)', value: metrics.leadsThisMonth, icon: Inbox, color: 'bg-teal-500' },
    { label: 'Quotes (This Month)', value: metrics.quotesThisMonth, icon: TrendingUp, color: 'bg-orange-500' },
  ];

  const reportsMoM = metrics.reportsPrevMonth > 0
    ? (((metrics.reportsThisMonth - metrics.reportsPrevMonth) / metrics.reportsPrevMonth) * 100).toFixed(0)
    : metrics.reportsThisMonth > 0 ? '+100' : '0';

  const dailyLoginsData = (metrics.dailyLogins || []).reverse().map(d => ({
    date: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    logins: d.logins
  }));

  return (
    <AgentLayout title="Metrics Dashboard">
      <div data-testid="admin-metrics-page">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="metrics-kpi-cards">
          {kpis.map(kpi => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white rounded-lg shadow-md p-4" data-testid={`metric-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 font-semibold uppercase">{kpi.label}</span>
                  <div className={`w-8 h-8 ${kpi.color} rounded-lg flex items-center justify-center`}>
                    <Icon size={16} className="text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpi.value}</p>
              </div>
            );
          })}
        </div>

        {/* MoM Comparison + Team Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="mom-comparison">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reports: Month-over-Month</h3>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-brand-blue">{metrics.reportsThisMonth}</p>
                <p className="text-xs text-gray-500">This Month</p>
              </div>
              <div className="text-center text-gray-400">vs</div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-400">{metrics.reportsPrevMonth}</p>
                <p className="text-xs text-gray-500">Last Month</p>
              </div>
              <div className={`text-center px-3 py-1 rounded-full text-sm font-bold ${Number(reportsMoM) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {Number(reportsMoM) >= 0 ? '+' : ''}{reportsMoM}%
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6" data-testid="team-breakdown">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Agents by Team</h3>
            <div className="space-y-3">
              {Object.entries(metrics.teamCounts || {}).map(([team, count]) => (
                <div key={team} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${team === 'HIG' ? 'bg-teal-500' : 'bg-purple-500'}`}></span>
                    <span className="font-medium text-sm">{team}</span>
                  </div>
                  <span className="text-xl font-bold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Login Activity */}
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="daily-logins-chart">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Daily Login Activity (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dailyLoginsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="logins" fill="#0047AB" name="Logins" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AgentLayout>
  );
};

export default AdminMetrics;
